import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  defaultColDef,
  columnDefs,
  frameworkComponents,
  tableActionsConfig,
  filtering,
  styling,
  serverPagination
} from '../config/table'
import { useHistory } from 'react-router-dom'
import { ns, STORE_KEY } from '../constants'
import { makeOnOutputRowSelected, useStoreSelector } from '@ublique/platform'
import { updateState } from 'utils/state'
import { fetchTableData } from '../actions'
import {
  initialState,
  reducers,
  selectors,
  spaceOccupancyStore
} from '../state'
import { getServerSideTableRequestBody } from 'pages/ExtraOutput/utils'
import { storeCollaborationStore } from 'pages/ExtraOutput/state'
import { isEmpty } from 'lodash'

export default function useTableConfig() {
  const [gridApi, setGridApi] = useState()
  const { t } = useTranslation('ExtraOutput')
  const translate = (n) => t(`${ns}.${n}`)
  const history = useHistory()

  const selectionParams = useStoreSelector(
    STORE_KEY,
    (state) => state.selection
  )

  const store = useStoreSelector(
    'storeCollaboration',
    (state) => state.filters.store
  )

  const handleLocalSelection = useCallback((node) => {
    // devo gestire la selezione locale per aggiornare lo stato, perché con la selezione
    // server side, se seleziono tutte le righe perdo i dati e mette un selectAll: true,
    // quindi non conosco più le stanze selezionate
    const { selected, data } = node
    const room = data.stanza

    // devo gestire anche il toggle delle righe
    storeCollaborationStore.update((prev) => {
      const currentRooms = prev.filters.selectedRooms || []

      if (
        selected &&
        !currentRooms.some((existingRoom) => existingRoom.id === room)
      ) {
        return {
          ...prev,
          filters: {
            ...prev.filters,
            selectedRooms: [...currentRooms, { id: room, text: room }]
          }
        }
      } else if (
        !selected &&
        currentRooms.some((existingRoom) => existingRoom.id === room)
      ) {
        return {
          ...prev,
          filters: {
            ...prev.filters,
            selectedRooms: currentRooms.filter(
              (existingRoom) => existingRoom.id !== room
            )
          }
        }
      }

      return prev
    })
  }, [])

  useEffect(() => {
    if (store && gridApi) {
      gridApi.deselectAll()
    }
  }, [gridApi, store])

  const rowSelected = (store, selection, reducer) => {
    const handleSelection = makeOnOutputRowSelected(store, selection, reducer)
    return (row) => {
      handleLocalSelection(row.node)
      handleSelection(row)
    }
  }

  const selection = {
    rowSelection: 'multiple',
    suppressRowClickSelection: false,
    onRowSelected: rowSelected(
      spaceOccupancyStore,
      selectors.getSelection,
      reducers.onRowSelection
    )
  }

  const onRedirect = useCallback(() => {
    const selectedRows = gridApi?.getSelectedRows() || []
    const rooms = selectedRows.map((row) => ({
      id: row.stanza,
      text: row.stanza
    }))
    // Sovrascrivo selectedRooms con solo le righe selezionate
    storeCollaborationStore.update((prev) => ({
      ...prev,
      filters: { ...prev.filters, selectedRooms: rooms }
    }))
    history.push('/extraOutput/FeedbackUtente')
  }, [history, gridApi])

  const onPaginationSizeChange = (size) => {
    updateState(spaceOccupancyStore, { pageSize: size })
    gridApi.paginationSetPageSize(size)
    gridApi.purgeServerSideCache()
  }

  const extraHeaderComponent = {
    extraHeaderComponentContainerClassName: 'extraComponent',
    extraHeaderComponent: {
      t,
      selectedRowsCount: selectionParams?.count,
      selectedRowsLabel: t(
        'ExtraOutput.StoreCollaboration.SELECTED_ROWS_COUNT',
        {
          rowsCount: selectionParams?.count
        }
      ),
      config: tableActionsConfig({
        t: translate,
        onRedirect,
        onPaginationSizeChange
      })
    }
  }

  useEffect(() => {
    if (gridApi && !isEmpty(store)) gridApi.purgeServerSideCache()
  }, [store, gridApi])

  useEffect(() => {
    return () => {
      spaceOccupancyStore.set(initialState)
    }
  }, [])

  const serverSideDatasource = {
    getRows: (params) => {
      const { request, successCallback } = params
      updateState(spaceOccupancyStore, { filters: request.filterModel })

      const selectedStore = storeCollaborationStore.get(
        (state) => state.filters.store
      )
      const pageSize = spaceOccupancyStore.get((state) => state.pageSize)

      //Evito la prima chiamata se lo store non è selezionato
      if (isEmpty(selectedStore)) {
        successCallback([], 0)
        return
      }

      const filterModel = request.filterModel

      const body = getServerSideTableRequestBody({
        ...request,
        filterModel,
        length: pageSize
      })

      if (!isEmpty(body.filter)) {
        if (body.filter.warning && !isEmpty(body.filter.warning.values)) {
          body.filter.warning = {
            ...body.filter.warning,
            values: [body.filter.warning.values[0] === 'true'] // trasformo in booleano
          }
        }
      }

      fetchTableData(body, selectedStore).then((response) => {
        if (isEmpty(response)) return
        const { items, metadata } = response
        const {
          pagination: { total: rowCount }
        } = metadata

        spaceOccupancyStore.update((prevState) => ({
          ...prevState,
          filterBody: body,
          selection: {
            ...prevState.selection,
            total: rowCount
          },
          items
        }))
        successCallback(items, rowCount)
      })
    }
  }

  return [
    {
      config: { sm: 4, md: 8, lg: 12 },
      tableProps: {
        getContextMenuItems: [],
        onGridReady: ({ api }) => setGridApi(api),
        columnDefs: columnDefs({ t: translate, store }),
        canShowCheckbox: true,
        serverSideDatasource,
        //getRowNodeId: () => uniqueId(),
        rowModelType: 'serverSide',
        frameworkComponents,
        defaultColDef,
        ...serverPagination,
        ...filtering,
        ...selection,
        ...extraHeaderComponent,
        ...styling
        // autoSizeColumnsOnFirstRender: true,
      }
    }
  ]
}
